/**
 * Chat API client — Phase 0 staff debug surface.
 *
 * Backed by the chat Django app at `/api/chat/*` which is mounted ONLY when
 * `CHAT_ASSISTANT_ENABLED=true` server-side AND the caller is `is_staff`
 * (PRD Section 3.8). The frontend additionally guards the route by an
 * `import.meta.env.VITE_CHAT_BETA_ENABLED === "true"` flag and a runtime
 * `useMeQuery().data?.isStaff` check.
 *
 * To enable locally:
 *   echo "VITE_CHAT_BETA_ENABLED=true" >> frontend/app/.env.local
 *
 * Default is OFF. Phase 0 ships with this off in every deployed env.
 */
import axios from "axios";
import { z } from "zod";

import api from "./base";
import { codecGet, isObject, toCamelDeep } from "@/api/codec";
import { parseCitations } from "@/components/chat/citations-schema";
import { parseToolResult } from "@/components/chat/tool-results-schemas";
import { parseToolCard } from "@/components/chat/tool-cards/schemas";
import { normalizePersistedTrace } from "@/components/chat/tool-trace/replay";
import type {
  ChatFeedbackRating,
  ChatFeedbackResponse,
  ChatReplayMessage,
  ChatThreadListItem,
  ChatThreadListResponse,
  ChatThreadReplayResponse,
  ChatTurnRequest,
  ChatTurnResponse,
} from "@/pages/Staff/Chat/types";

/**
 * Typed wrapper for chat-endpoint failures.
 *
 * Why this exists: the global axios response interceptor in `@/api/base`
 * only camelizes 2xx responses — error bodies pass through snake_case.
 * Consumers (Page.tsx::onError) need the BE's stable `error_code` to
 * branch on (e.g. `chat_beta_not_enabled_for_org` triggers the
 * "request beta access" CTA per AC-29). Doing that snake→camel mapping
 * in every onError handler would silently regress the moment one of
 * them forgot. Centralizing here means callers get a typed
 * `ChatApiError` with `errorCode` already normalized.
 */
export class ChatApiError extends Error {
  /** HTTP status from the response, or 0 for network errors. */
  status: number;

  /**
   * Stable, machine-readable code from the BE's `{detail, error_code}`
   * envelope (see backend/server/chat/exceptions.py). Undefined for
   * generic network failures or non-chat 5xx responses that don't
   * carry the envelope.
   */
  errorCode?: string;

  /** Human-readable detail string from the BE, if present. */
  detail?: string;

  constructor(opts: { status: number; errorCode?: string; detail?: string }) {
    super(opts.detail ?? opts.errorCode ?? `HTTP ${opts.status}`);
    this.name = "ChatApiError";
    this.status = opts.status;
    this.errorCode = opts.errorCode;
    this.detail = opts.detail;
  }
}

/**
 * Translate an axios error into a ChatApiError. Error responses bypass
 * the camelize interceptor (which only fires on 2xx), so the body keys
 * are still snake_case here — read `error_code` not `errorCode`.
 */
const toChatApiError = (err: unknown): ChatApiError => {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const data = err.response?.data as
      | { detail?: string; error_code?: string }
      | undefined;
    return new ChatApiError({
      status,
      errorCode: data?.error_code,
      detail: typeof data?.detail === "string" ? data.detail : undefined,
    });
  }
  return new ChatApiError({ status: 0 });
};

/**
 * Wave-11 Track H wire-flatten helper.
 *
 * The page-layer ergonomic is `ChatTurnRequest.metadata = { seed_row_id,
 * seed_building_id }` — a single grouped knob that lights up the row-
 * explain fast path. The wire-layer ergonomic is `{seed_row_id,
 * seed_building_id}` as TOP-LEVEL siblings of `message` /
 * `building_id`, because that's the shape
 * `chat.serializers.ChatTurnRequestSerializer` declares (and
 * `views._resolve_turn_inputs` reads via `data.get("seed_row_id")`).
 *
 * Wave-11 live-MCP-smoke (LD-1) caught the original implementation
 * shipping the body with `metadata: {...}` nested — the BE never saw
 * the fields, the fast path silently degraded to the agent loop, and
 * the agent had no row context so it refused. The DRY-across-surfaces
 * invariant (`feedback_dry_across_surfaces`) means this flattener is
 * defined ONCE and reused across the two outbound paths — the axios
 * single-shot below and the bypass-axios SSE in `chatStream.ts` (which
 * re-imports and re-uses this helper).
 *
 * Keys are wire-shape snake_case at the call site so axios's global
 * `decamelizeKeys` is a no-op on them — see
 * `feedback_camelcase_axios_jsonfield` for why we don't lean on the
 * camelizer for BE-coupled content.
 */
export const flattenSeedFieldsToWireBody = (
  req: ChatTurnRequest,
): Record<string, unknown> => {
  const { metadata, ...rest } = req;
  if (!metadata) return { ...rest };
  // Only emit a key when the page-layer supplied a defined value.
  // Avoids stamping a `seed_building_id: undefined` over JSON, which
  // would survive `JSON.stringify` as the key being elided anyway —
  // but the explicit guard documents intent and lets the wire
  // assertion tests treat "absent" as a positive contract.
  const out: Record<string, unknown> = { ...rest };
  if (metadata.seed_row_id !== undefined) {
    out.seed_row_id = metadata.seed_row_id;
  }
  if (metadata.seed_building_id !== undefined) {
    out.seed_building_id = metadata.seed_building_id;
  }
  // LL87 chat-intro (FR-CI-9): the planner selection rides as a
  // TOP-LEVEL sibling of `message` — same flattening contract as
  // seed_row_id/seed_building_id (the BE serializer does NOT unwrap a
  // `metadata` envelope; see the Track H LD-1 note above).
  if (metadata.seed_building_ids !== undefined) {
    out.seed_building_ids = metadata.seed_building_ids;
  }
  return out;
};

/**
 * Send one prompt, get one synchronous response. No SSE in Phase 0.
 *
 * The base axios interceptor decamelizes the request body, so the wire
 * payload is `{ thread_id, message, building_id }` even though the JS-side
 * shape is camelCase. Failures throw a `ChatApiError` carrying the BE's
 * `error_code` so the caller can branch on it without grovelling through
 * axios internals.
 *
 * Wave-11 Track H: any `req.metadata.seed_*` fields are flattened to
 * top-level body keys by {@link flattenSeedFieldsToWireBody} — the BE's
 * `ChatTurnRequestSerializer` reads `seed_row_id` / `seed_building_id`
 * as siblings of `message`, not nested under `metadata`.
 */
export const postChatTurn = (req: ChatTurnRequest): Promise<ChatTurnResponse> =>
  api
    .post<ChatTurnResponse>("chat/turns/", flattenSeedFieldsToWireBody(req))
    .then((r) => r.data)
    .catch((err) => {
      throw toChatApiError(err);
    });

/**
 * Per-message thumbs feedback — Phase 1 W2 (PRD AC-39).
 *
 * UPSERT semantics: the BE returns 201 when the (user, message) row is
 * created and 200 when it's updated. The body shape is identical
 * either way (`ChatFeedbackResponse`), so callers don't need to
 * discriminate. Rating is the BE-enforced ±1 contract — passing 0
 * yields a 400. Clearing a rating ("cancel a thumb") is intentionally
 * out of scope: BE has no DELETE yet, so the FE treats a same-thumb
 * re-click as a visual no-op (Phase 2 follow-up).
 *
 * Wire body is `{rating, comment?}` after the request decamelizer runs
 * (no key change — both fields are already snake-compatible). We omit
 * `comment` entirely when undefined to keep the wire payload minimal;
 * Phase 1 W2 ships thumbs-only per the DA's `FE_NEXT` directive.
 */
export const postChatFeedback = (
  messageId: string,
  rating: ChatFeedbackRating,
  comment?: string,
): Promise<ChatFeedbackResponse> =>
  api
    .post<ChatFeedbackResponse>(
      `chat/messages/${messageId}/feedback/`,
      comment !== undefined ? { rating, comment } : { rating },
    )
    .then((r) => r.data)
    .catch((err) => {
      throw toChatApiError(err);
    });

/**
 * Fetch the caller's existing rating for a message. Returns the
 * `ChatFeedbackResponse` on hit (200) or `null` on miss (404 with
 * `error_code: "feedback_not_found"`). Any other failure is surfaced
 * as a `ChatApiError` so the caller can decide whether to retry,
 * silently swallow, or surface it.
 *
 * Why null-on-404 instead of throwing: rendering thumbs is a hover-
 * grade affordance — the page should not block on "no feedback yet".
 * Centralizing the 404 → null mapping here keeps every caller from
 * re-implementing the same swallow.
 */
export const getChatFeedback = (
  messageId: string,
): Promise<ChatFeedbackResponse | null> =>
  api
    .get<ChatFeedbackResponse>(`chat/messages/${messageId}/feedback/`)
    .then((r) => r.data)
    .catch((err) => {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      throw toChatApiError(err);
    });

/**
 * Phase 1 W3-A — durable thread persistence.
 *
 * List endpoint: returns the caller's threads, paginated by cursor.
 * The FE thread-list sidebar consumes this on mount; it then keys
 * off the active threadId in localStorage to highlight the current
 * row.
 *
 * Optional filters:
 * - ``buildingId`` restricts the list to threads pinned to that
 *   building (used by the building detail page once W3-A's panel
 *   integration lands; the staff /chat shell ignores it for now).
 * - ``cursor`` is the opaque ``next`` field from a prior page.
 *
 * Failures share the ``ChatApiError`` envelope so callers can branch
 * on the same ``error_code`` taxonomy as the chat-turn endpoints.
 */
export const listChatThreads = (params?: {
  buildingId?: string;
  cursor?: string;
  /**
   * Stream 12 — opt-in limit/offset pagination for the
   * ``<ChatHistory>`` modal. Cursor pagination remains the default
   * for the staff sidebar; passing ``limit`` or ``offset`` switches
   * the BE to limit/offset mode (see ``ChatThreadListView``).
   */
  limit?: number;
  offset?: number;
  /**
   * Stream 13 reservation — full-text search. Currently accepted-
   * but-ignored by the BE; passing it today is a no-op so the FE
   * can wire the UI ahead of the BE landing.
   */
  search?: string;
}): Promise<ChatThreadListResponse> =>
  api
    .get<ChatThreadListResponse>("chat/threads/", { params })
    .then((r) => r.data)
    .catch((err) => {
      throw toChatApiError(err);
    });

/**
 * Hard-delete a thread (W8). Cascades on the BE: the thread row, its
 * ``ChatMessage`` children, ``ChatAction`` rows, and any
 * ``MessageFeedback`` are all removed in one DB call.
 *
 * Authorization: the BE additionally checks ``thread.user ==
 * request.user`` before deleting. A thread the caller doesn't own
 * collapses to ``thread_not_found`` (anti-enumeration), surfaced as
 * a ``ChatApiError`` with that ``errorCode``.
 *
 * Returns void on 204. Failures throw a ``ChatApiError`` so the
 * caller can branch on ``errorCode`` to roll back an optimistic UI
 * update.
 */
export const deleteChatThread = (threadId: string): Promise<void> =>
  api
    .delete(`chat/threads/${threadId}/`)
    .then(() => undefined)
    .catch((err) => {
      throw toChatApiError(err);
    });

// ---------------------------------------------------------------------
// Replay codec — Phase 1 (wire-codec boundary).
//
// The replay endpoint reads four Django JSONField columns
// (``citations``, ``tool_results``, ``orchestrator_cards``,
// ``orchestrator_trace``) whose contents are BE-owned snake_case
// payloads. The legacy global axios interceptor recursively camelized
// those payloads — corrupting the semantic snake_case discriminators
// (``render_kind`` → ``renderKind``, ``card_kind`` → ``cardKind``) and
// producing the dual-shape bug the CTO note in ``api/base.ts``
// describes (the SAME payloads arrive verbatim snake_case via SSE).
//
// This codec migrates the endpoint to ``codecGet`` (which DISABLES the
// global camelizer for this response) and runs the SAME slice codecs the
// SSE path uses over each JSONField column:
//   - ``citations``          → ``parseCitations``
//   - ``tool_results``       → ``parseToolResult`` (per entry)
//   - ``orchestrator_cards`` → ``parseToolCard`` (per entry)
//   - ``orchestrator_trace`` → ``normalizePersistedTrace`` (bulk)
// The flat (non-JSONField) message + thread fields are plain DRF
// serializer output, so we camelize THOSE with the shared
// ``toCamelDeep`` to preserve the camelCase shape consumers expect.
// ---------------------------------------------------------------------

const replayMessageSchema = z
  .unknown()
  .transform((raw, ctx): ChatReplayMessage => {
    if (!isObject(raw)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "replay message: expected object",
      });
      return z.NEVER;
    }
    // Flat scalar fields are plain DRF output — camelize verbatim. The
    // four JSONField columns are pulled aside and routed through the
    // slice codecs so their snake_case discriminators survive.
    const {
      citations,
      tool_results: toolResultsRaw,
      orchestrator_cards: orchestratorCardsRaw,
      orchestrator_trace: orchestratorTraceRaw,
      ...flat
    } = raw;
    const camelFlat = toCamelDeep(flat) as Omit<
      ChatReplayMessage,
      "citations" | "toolResults" | "orchestratorCards" | "orchestratorTrace"
    >;
    return {
      ...camelFlat,
      citations: parseCitations(citations),
      toolResults: Array.isArray(toolResultsRaw)
        ? toolResultsRaw
            .map((tr) => parseToolResult(tr))
            .filter((tr): tr is NonNullable<typeof tr> => tr !== null)
        : [],
      orchestratorCards: Array.isArray(orchestratorCardsRaw)
        ? orchestratorCardsRaw
            .map((c) => parseToolCard(c))
            .filter((c): c is NonNullable<typeof c> => c !== null)
        : [],
      orchestratorTrace: Array.isArray(orchestratorTraceRaw)
        ? [...normalizePersistedTrace(orchestratorTraceRaw)]
        : [],
    };
  });

const replayResponseSchema = z
  .unknown()
  .transform((raw, ctx): ChatThreadReplayResponse => {
    if (!isObject(raw)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "replay response: expected object",
      });
      return z.NEVER;
    }
    const messagesRaw = Array.isArray(raw.messages) ? raw.messages : [];
    const messages = messagesRaw
      .map((m) => replayMessageSchema.safeParse(m))
      .filter((p) => p.success)
      .map((p) => (p as { data: ChatReplayMessage }).data);
    // ``thread`` is plain DRF output (no opaque JSONField payload) — the
    // shared deep camelizer reproduces the camelCase the global
    // interceptor used to apply (``last_message_at`` → ``lastMessageAt``,
    // etc.).
    const thread = toCamelDeep(raw.thread) as ChatThreadListItem;
    return { thread, messages };
  });

/**
 * Replay a thread's full transcript. Used by the sidebar to rehydrate
 * the page's local ``messages`` state when the user clicks a row.
 *
 * The BE returns the messages in chronological order; the FE
 * appends them to ``messages`` in that order so the transcript
 * renders top-to-bottom exactly as it would have during the live
 * conversation.
 *
 * Phase 1 (wire-codec boundary): routed through ``codecGet`` + the
 * ``replayResponseSchema`` so the JSONField payloads (cards / trace /
 * tool-results / citations) are parsed by the SAME slice codecs the SSE
 * path uses — the replay surface and the live surface can no longer
 * disagree on key spelling.
 *
 * Cross-tenant accesses collapse to a single ``thread_not_found``
 * error_code (anti-enumeration).
 */
export const replayChatThread = (
  threadId: string,
): Promise<ChatThreadReplayResponse> =>
  codecGet(`chat/threads/${threadId}/messages/`, replayResponseSchema).catch(
    (err) => {
      throw toChatApiError(err);
    },
  );

export type {
  ChatTurnRequest,
  ChatTurnMetadata,
  ChatTurnResponse,
  ChatFeedbackRating,
  ChatFeedbackResponse,
  ChatThreadListItem,
  ChatThreadListResponse,
  ChatThreadReplayResponse,
  ChatReplayMessage,
  Citation,
  ChatConfidence,
} from "@/pages/Staff/Chat/types";

// ---------------------------------------------------------------------
// Phase 2 Action A — chat-staged write confirm / cancel
// ---------------------------------------------------------------------
//
// The chat tool ``save_compliance_answer`` stages a ChatAction in
// ``state="pending"`` and returns its id. The FE renders a
// <ChatActionConfirmation> card; user clicks Confirm / Cancel and the
// card POSTs here. Both endpoints are idempotent and use the same
// IsStaffWithChatBetaFlag gate as the chat-turn endpoints. Wire shape:
//
//   POST /api/chat/actions/<id>/confirm/
//     200  → {state: "confirmed", new_answer, previous_answer,
//             undo_until_iso}
//     409  → {error_code: "action_cancelled" | "action_expired"}
//     410  → {error_code: "action_expired"}
//     404  → {error_code: "action_not_found" | "building_gone"}
//
//   POST /api/chat/actions/<id>/cancel/
//     200  → {state: "cancelled", new_answer, previous_answer}
//     409  → {error_code: "undo_window_closed" | "action_expired"}

export interface ChatActionConfirmResponse {
  state: "confirmed";
  new_answer: string | null;
  previous_answer: string | null;
  action_id: string;
  undo_until_iso?: string;
}

export interface ChatActionCancelResponse {
  state: "cancelled";
  new_answer: string | null;
  previous_answer: string | null;
  action_id: string;
}

/**
 * Confirm a pending ChatAction. Idempotent: a re-POST returns the
 * same payload without re-executing the underlying mutation.
 */
export const confirmChatAction = (
  actionId: string,
): Promise<ChatActionConfirmResponse> =>
  api
    .post<ChatActionConfirmResponse>(`chat/actions/${actionId}/confirm/`, {})
    .then((r) => r.data)
    .catch((err) => {
      throw toChatApiError(err);
    });

/**
 * Cancel (pre-confirm) or undo (post-confirm, within 10s) a
 * ChatAction. The server picks the right path based on action state.
 */
export const cancelChatAction = (
  actionId: string,
): Promise<ChatActionCancelResponse> =>
  api
    .post<ChatActionCancelResponse>(`chat/actions/${actionId}/cancel/`, {})
    .then((r) => r.data)
    .catch((err) => {
      throw toChatApiError(err);
    });
